import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Menu, X, BookOpen, Globe, ChevronDown, Shield, HelpCircle, UserCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { NotificationBell } from './NotificationBell';

const languages = [
  { code: 'es', name: 'Español', flag: '🇪🇸' },
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'fr', name: 'Français', flag: '🇫🇷' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
];

export const Navbar = () => {
  const { t, i18n } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const currentLang = languages.find(l => l.code === i18n.language) || languages[0];

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Brand mark: green rounded square with yellow book icon + green
              wordmark (matches the official A Gift of Memories logo). */}
          <Link to="/" className="flex items-center gap-2.5" data-testid="logo-link">
            <span
              aria-hidden="true"
              className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-primary shadow-sm"
            >
              <BookOpen className="w-5 h-5" style={{ color: '#FFCC00' }} strokeWidth={1.8} />
            </span>
            <span className="font-heading text-xl font-semibold text-primary">A Gift of Memories</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            <Link to="/" className="nav-link" data-testid="nav-home">
              {t('nav.home')}
            </Link>
            <a href="#how-it-works" className="nav-link" data-testid="nav-how-it-works">
              {t('nav.howItWorks')}
            </a>
            <a href="#pricing" className="nav-link" data-testid="nav-pricing">
              {t('nav.pricing')}
            </a>
          </div>

          {/* Right side */}
          <div className="hidden md:flex items-center gap-4">
            {/* Language Selector */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-2" data-testid="language-selector">
                  <Globe className="w-4 h-4" />
                  <span>{currentLang.flag}</span>
                  <ChevronDown className="w-3 h-3" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {languages.map((lang) => (
                  <DropdownMenuItem
                    key={lang.code}
                    onClick={() => i18n.changeLanguage(lang.code)}
                    data-testid={`lang-${lang.code}`}
                  >
                    <span className="mr-2">{lang.flag}</span>
                    {lang.name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Auth buttons */}
            {user ? (
              <div className="flex items-center gap-4">
                {user.is_admin && (
                  <Button variant="ghost" onClick={() => navigate('/admin')} data-testid="nav-admin" className="gap-2 text-primary">
                    <Shield className="w-4 h-4" />
                    {t('nav.admin')}
                  </Button>
                )}
                <Button variant="ghost" onClick={() => navigate('/dashboard')} data-testid="nav-dashboard">
                  {t('nav.dashboard')}
                </Button>
                {/* In-app notifications bell (only visible to logged-in users) */}
                <NotificationBell />
                <Button variant="ghost" onClick={() => navigate('/perfil')} data-testid="nav-profile" className="gap-1" title="Mi perfil">
                  <UserCircle className="w-4 h-4" />
                  Mi perfil
                </Button>
                <Button variant="ghost" onClick={() => navigate('/ayuda')} data-testid="nav-help" className="gap-1">
                  <HelpCircle className="w-4 h-4" />
                  {t('nav.help', 'Ayuda')}
                </Button>
                <Button variant="outline" onClick={handleLogout} data-testid="nav-logout">
                  {t('nav.logout')}
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <Button variant="ghost" onClick={() => navigate('/login')} data-testid="nav-login">
                  {t('nav.login')}
                </Button>
                <Button 
                  className="bg-primary hover:bg-primary/90 rounded-full px-6" 
                  onClick={() => navigate('/register')}
                  data-testid="nav-register"
                >
                  {t('nav.register')}
                </Button>
              </div>
            )}
          </div>

          {/* Mobile menu button */}
          <button
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-button"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 py-4 px-4">
          <div className="flex flex-col gap-4">
            <Link to="/" className="py-2 text-gray-700" onClick={() => setMobileMenuOpen(false)}>
              {t('nav.home')}
            </Link>
            <a href="#how-it-works" className="py-2 text-gray-700" onClick={() => setMobileMenuOpen(false)}>
              {t('nav.howItWorks')}
            </a>
            <a href="#pricing" className="py-2 text-gray-700" onClick={() => setMobileMenuOpen(false)}>
              {t('nav.pricing')}
            </a>
            <div className="border-t border-gray-100 pt-4 flex flex-col gap-3">
              {/* Language selector mobile */}
              <div className="flex gap-2">
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => { i18n.changeLanguage(lang.code); setMobileMenuOpen(false); }}
                    className={`px-3 py-1 rounded-full text-sm ${i18n.language === lang.code ? 'bg-primary text-white' : 'bg-gray-100'}`}
                  >
                    {lang.flag}
                  </button>
                ))}
              </div>
              {user ? (
                <>
                  {user.is_admin && (
                    <Link to="/admin" onClick={() => setMobileMenuOpen(false)}>
                      <Button variant="outline" className="w-full gap-2 text-primary">
                        <Shield className="w-4 h-4" />
                        {t('nav.admin')}
                      </Button>
                    </Link>
                  )}
                  <Link to="/dashboard" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="outline" className="w-full">{t('nav.dashboard')}</Button>
                  </Link>
                  <Link to="/perfil" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="outline" className="w-full gap-2" data-testid="mobile-nav-profile">
                      <UserCircle className="w-4 h-4" />
                      Mi perfil
                    </Button>
                  </Link>
                  <Link to="/ayuda" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="outline" className="w-full gap-2">
                      <HelpCircle className="w-4 h-4" />
                      {t('nav.help', 'Ayuda')}
                    </Button>
                  </Link>
                  <Button onClick={() => { handleLogout(); setMobileMenuOpen(false); }} className="w-full">
                    {t('nav.logout')}
                  </Button>
                </>
              ) : (
                <>
                  <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                    <Button variant="outline" className="w-full">{t('nav.login')}</Button>
                  </Link>
                  <Link to="/register" onClick={() => setMobileMenuOpen(false)}>
                    <Button className="w-full bg-primary">{t('nav.register')}</Button>
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export const Footer = () => {
  const { t } = useTranslation();
  const year = new Date().getFullYear();

  const legalLinks = [
    { to: '/aviso-legal', label: t('footer.legal.notice', 'Aviso Legal') },
    { to: '/politica-de-privacidad', label: t('footer.legal.privacy', 'Política de Privacidad') },
    { to: '/politica-de-cookies', label: t('footer.legal.cookies', 'Política de Cookies') },
    { to: '/terminos-y-condiciones', label: t('footer.legal.terms', 'Términos y Condiciones') },
  ];

  return (
    <footer className="bg-primary text-white py-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-6">
          {/* Brand mark — inverted variant for the dark footer: cream/white
              rounded square so the icon stays yellow but the box is visible. */}
          <div className="flex items-center gap-2.5">
            <span
              aria-hidden="true"
              className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-white/10 ring-1 ring-white/15"
            >
              <BookOpen className="w-5 h-5" style={{ color: '#FFCC00' }} strokeWidth={1.8} />
            </span>
            <span className="font-heading text-xl font-semibold">A Gift of Memories</span>
          </div>
          <p className="font-handwritten text-lg text-white/80">{t('footer.tagline')}</p>
        </div>

        <nav aria-label="Enlaces legales" className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 mb-5" data-testid="footer-legal-links">
          {legalLinks.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className="text-xs md:text-sm text-white/80 hover:text-white hover:underline underline-offset-4 transition-colors"
              data-testid={`footer-link-${l.to.replace('/', '')}`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="border-t border-white/10 pt-5 flex flex-col md:flex-row items-center justify-between gap-2 text-center text-[11px] md:text-xs text-white/60">
          <p>© {year} A Gift of Memories. {t('footer.rights')}</p>
          <p>
            Incubedreams S.L. · NIF B19718907 · C/ Luchana 27, 28010 Madrid · Tel. +34 919 93 34 27 · info@agiftofmemories.com
          </p>
        </div>
      </div>
    </footer>
  );
};
